import { useEffect, useLayoutEffect, useRef, useState, type ComponentType, type ReactNode, type SVGProps } from 'react';
import { createPortal } from 'react-dom';
import {
  Button,
  Input,
  Spinner,
  StatusBadge,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from '@immo/ui-kit';
import type {
  FleetCommandView,
  FleetJobView,
  FleetLaneCommandVerb,
  FleetLaneEngine,
  FleetMachineOverview,
  FleetProfileState,
  FleetProfileStatus,
} from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { useLocaleFormat } from '@/i18n/use-locale-format';
import { FlowIcon, SearchIcon, ServerIcon, TerminalIcon } from '@/components/layout/icons';
import type { ApprovalVerb } from './approval-dialog';
import { FleetApprovalBadge, FleetStateCircle, STATE_LABEL_CLASS } from './fleet-badges';
import { useCommandVerbCopy, useVerbLabel, type FormCommandVerb, type MachineCommandVerb } from './command-verbs';
import { useCommandStepLabel } from './command-outcome-badge';
import { commandStep } from './command-steps';
import { pendingHostCommandFor, pendingLaneCommandFor } from './pending-commands';
import { isStoppedByOperator } from './profile-commands';
import { SuspectBadge } from './job-pulse';
import { TileHealthRow } from './health/tile-health-row';
import { FleetThrottleBadge } from './health/throttle-badge';
import { Segments } from './segments';

/** The toolbar's state filter: every lane, or one of the four states an operator acts on. */
export type LaneStateFilter = 'all' | Exclude<FleetProfileState, 'pending'>;

export const LANE_STATE_FILTERS: ReadonlyArray<LaneStateFilter> = [
  'all',
  'working',
  'idle',
  'waiting-on-human',
  'stopped',
];

/** Whether a lane survives the toolbar: its state, and the query against its name and title. */
export function laneMatches(profile: FleetProfileStatus, state: LaneStateFilter, query: string): boolean {
  if (state !== 'all' && profile.state !== state) return false;

  const needle = query.trim().toLowerCase();

  if (needle === '') return true;

  return profile.profile.toLowerCase().includes(needle) || (profile.title ?? '').toLowerCase().includes(needle);
}

export type FleetSelection = { readonly machineId: string; readonly lane: string | null } | null;

type Actions = {
  readonly onSelectMachine: (machineId: string) => void;
  readonly onSelectLane: (machineId: string, lane: string) => void;
  readonly onApproval: (machineId: string, verb: ApprovalVerb) => void;
  readonly onHostVerb: (machineId: string, verb: MachineCommandVerb) => void;
  readonly onFormVerb: (machineId: string, verb: FormCommandVerb) => void;
  readonly onHostEngine: (machineId: string) => void;
  readonly onLaneVerb: (machineId: string, lane: string, verb: FleetLaneCommandVerb) => void;
  readonly onLaneEngine: (machineId: string, lane: string, current: FleetLaneEngine | null | undefined) => void;
};

type Props = Actions & {
  readonly pending: ReadonlyArray<FleetMachineOverview>;
  readonly machines: ReadonlyArray<FleetMachineOverview>;
  readonly selection: FleetSelection;
  readonly canCommand: boolean;
  /** Every command the page knows of, any machine — what turns a row's icons into its in-flight step. */
  readonly commands: ReadonlyArray<FleetCommandView>;
  /** The fleet's live jobs; a machine holding a suspect one says so on its row (fleet-agent ADR 0012). */
  readonly jobs?: ReadonlyArray<FleetJobView>;
  readonly stateFilter: LaneStateFilter;
  readonly query: string;
};

const COLUMN_COUNT = 6;

/**
 * The fleet as one table (OPS-128): a machine row heads each group, a lane row per reported lane follows. Every action
 * an operator takes is an icon on its row — the side panel only shows details. Groups and their order are the page's
 * (`groupAndSort`): awaiting approval on top, then the machines by name.
 *
 * Carries NO page title of its own: the topbar already reads `Fleet <n>`.
 */
export function MachineTable({
  pending,
  machines,
  selection,
  canCommand,
  commands,
  jobs = [],
  stateFilter,
  query,
  ...actions
}: Props) {
  const t = useTranslations('pages.admin.fleet');
  const tTable = useTranslations('pages.admin.fleet.table');
  const filtering = stateFilter !== 'all' || query.trim() !== '';

  const group = (list: ReadonlyArray<FleetMachineOverview>) =>
    list.map((machine) => (
      <MachineGroup
        key={machine.id}
        machine={machine}
        selection={selection}
        canCommand={canCommand}
        commands={commands.filter((command) => command.machineId === machine.id)}
        suspects={jobs.some((job) => job.machineId === machine.id && job.suspect !== null)}
        lanes={machine.profiles.filter((profile) => laneMatches(profile, stateFilter, query))}
        filtering={filtering}
        actions={actions}
      />
    ));

  return (
    <Table>
      <TableHeader className="hidden sm:table-header-group">
        <tr className="h-8 text-xs font-medium uppercase tracking-wide">
          <TableHead className="w-44 pl-10">{tTable('column-state')}</TableHead>
          <TableHead className="w-44">{tTable('column-lane')}</TableHead>
          <TableHead>{tTable('column-activity')}</TableHead>
          <TableHead className="w-28">{tTable('column-engine')}</TableHead>
          <TableHead className="w-24">{tTable('column-updated')}</TableHead>
          <TableHead className="w-40 pr-4 text-right">
            <span className="sr-only">{tTable('column-actions')}</span>
          </TableHead>
        </tr>
      </TableHeader>
      {pending.length > 0 && (
        <>
          <GroupHeader label={t('group-pending')} count={pending.length} pending />
          {group(pending)}
        </>
      )}
      <GroupHeader label={t('group-machines')} count={machines.length} />
      {group(machines)}
    </Table>
  );
}

function GroupHeader({ label, count, pending = false }: { label: string; count: number; pending?: boolean }) {
  return (
    <TableBody>
      <tr>
        <th colSpan={COLUMN_COUNT} scope="colgroup" className="px-4 pb-2 pt-4 text-left font-normal">
          <span className="flex items-center gap-2">
            <span
              className={cn(
                'text-xs font-semibold uppercase tracking-wide',
                pending ? 'text-warning-700' : 'text-text-tertiary',
              )}
            >
              {label}
            </span>
            <span
              className={cn(
                'inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-xs tabular-nums',
                pending ? 'bg-warning-100 text-warning-700' : 'bg-neutral-100 text-text-tertiary',
              )}
            >
              {count}
            </span>
          </span>
        </th>
      </tr>
    </TableBody>
  );
}

function MachineGroup({
  machine,
  selection,
  canCommand,
  commands,
  suspects,
  lanes,
  filtering,
  actions,
}: {
  readonly machine: FleetMachineOverview;
  readonly selection: FleetSelection;
  readonly canCommand: boolean;
  readonly commands: ReadonlyArray<FleetCommandView>;
  readonly suspects: boolean;
  readonly lanes: ReadonlyArray<FleetProfileStatus>;
  readonly filtering: boolean;
  readonly actions: Actions;
}) {
  const t = useTranslations('pages.admin.fleet');
  const tTable = useTranslations('pages.admin.fleet.table');
  const format = useLocaleFormat();
  const ref = useRef<HTMLTableRowElement>(null);
  const isPending = machine.approval === 'pending';
  const approved = machine.approval === 'approved';
  const machineSelected = selection?.machineId === machine.id && selection.lane === null;

  // A deep link can name a machine far down the table; bring it into view so the row the panel belongs to is the
  // one the operator can see.
  useEffect(() => {
    if (selection?.machineId === machine.id) ref.current?.scrollIntoView?.({ block: 'nearest' });
  }, [selection?.machineId, machine.id]);

  return (
    <TableBody>
      <tr
        ref={ref}
        aria-current={machineSelected ? 'true' : undefined}
        className={cn(
          'relative border-t border-border',
          machineSelected ? 'bg-selected-bg' : isPending ? 'bg-warning-50' : 'bg-neutral-50',
        )}
      >
        <td colSpan={COLUMN_COUNT} className="relative px-4 py-3">
          {machine.stale && <span aria-hidden className="absolute inset-y-0 left-0 w-[3px] bg-danger-500" />}
          <div className="flex min-w-0 flex-col gap-3 lg:flex-row lg:items-center lg:gap-6">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                <ServerIcon
                  className={cn('h-4 w-4 shrink-0', machine.stale ? 'text-danger-500' : 'text-text-tertiary')}
                  aria-hidden
                />
                <Button
                  type="button"
                  variant="link"
                  className="min-w-0 justify-start truncate text-base font-semibold text-text-primary underline decoration-neutral-300 underline-offset-[3px]"
                  aria-label={tTable('open-machine', { machine: machine.name })}
                  onClick={() => actions.onSelectMachine(machine.id)}
                >
                  {machine.name}
                </Button>
                {!approved && <FleetApprovalBadge approval={machine.approval} />}
                {machine.stale && <StatusBadge label={t('stale')} tone="danger" dot />}
                {suspects && <SuspectBadge />}
                {approved && machine.health?.throttle != null && (
                  <FleetThrottleBadge throttle={machine.health.throttle} />
                )}
                {filtering && (
                  <span className="text-sm text-text-tertiary">
                    {tTable('lanes-shown', {
                      shown: String(lanes.length),
                      total: String(machine.profiles.length),
                    })}
                  </span>
                )}
              </div>
              <div className="flex min-w-0 items-center gap-2 pl-6 text-sm text-text-tertiary">
                <span className="min-w-0 truncate font-mono">{machine.hostname}</span>
                <span className="shrink-0 text-text-placeholder">·</span>
                <span className={cn('shrink-0', machine.stale && 'font-medium text-danger-700')}>
                  {format.ago(machine.lastSeenAt)}
                </span>
                <span className="hidden shrink-0 text-text-placeholder sm:inline">·</span>
                <span className="hidden shrink-0 font-mono sm:inline">{machine.platform}</span>
              </div>
            </div>
            {approved && (
              <div className="hidden sm:block lg:w-[520px] lg:shrink-0">
                <TileHealthRow health={machine.health ?? null} stale={machine.stale} compact />
              </div>
            )}
            {canCommand && (
              <div className="flex shrink-0 justify-end">
                <HostActions machine={machine} pending={pendingHostCommandFor(commands)} actions={actions} />
              </div>
            )}
          </div>
        </td>
      </tr>
      {machine.profiles.length === 0 ? (
        <tr>
          <td colSpan={COLUMN_COUNT} className="px-4 py-3 pl-10 text-sm text-text-placeholder">
            {t('no-profiles')}
          </td>
        </tr>
      ) : (
        lanes.map((profile) => (
          <LaneRow
            key={profile.profile}
            machine={machine}
            profile={profile}
            selected={selection?.machineId === machine.id && selection.lane === profile.profile}
            canCommand={canCommand && approved}
            pending={pendingLaneCommandFor(commands, profile.profile)}
            actions={actions}
          />
        ))
      )}
    </TableBody>
  );
}

function LaneRow({
  machine,
  profile,
  selected,
  canCommand,
  pending,
  actions,
}: {
  readonly machine: FleetMachineOverview;
  readonly profile: FleetProfileStatus;
  readonly selected: boolean;
  readonly canCommand: boolean;
  readonly pending: FleetCommandView | null;
  readonly actions: Actions;
}) {
  const t = useTranslations('pages.admin.fleet.state');
  const format = useLocaleFormat();
  const open = () => actions.onSelectLane(machine.id, profile.profile);
  const engine = profile.engineRotation?.running ?? profile.engine?.engine ?? null;

  return (
    <TableRow
      interactive
      selected={selected}
      aria-current={selected ? 'true' : undefined}
      onClick={open}
      className={cn('border-t border-neutral-100 bg-surface', machine.stale && 'opacity-50')}
    >
      <td className="hidden py-2 pl-10 pr-3 sm:table-cell">
        <span className="flex items-center gap-2">
          <FleetStateCircle state={profile.state} className="h-2.5 w-2.5" />
          <span className={cn('truncate text-base-sm font-medium', STATE_LABEL_CLASS[profile.state])}>
            {t(profile.state)}
          </span>
        </span>
      </td>
      <td className="w-full max-w-0 py-2 pl-4 pr-3 sm:w-auto sm:pl-0">
        <span className="flex min-w-0 items-center gap-2">
          <FleetStateCircle state={profile.state} className="h-2.5 w-2.5 sm:hidden" />
          {/* The row is the click target for the pointer; this is the one for the keyboard. */}
          <Button
            type="button"
            variant="link"
            className="min-w-0 justify-start truncate font-mono text-base-sm font-medium text-text-primary no-underline hover:underline"
            onClick={(event) => {
              event.stopPropagation();
              open();
            }}
          >
            {profile.profile}
          </Button>
          <span className={cn('shrink-0 text-sm sm:hidden', STATE_LABEL_CLASS[profile.state])}>{t(profile.state)}</span>
        </span>
        <span className="block truncate pl-[18px] text-sm text-text-secondary sm:hidden">{profile.title ?? '—'}</span>
      </td>
      <td className="hidden max-w-0 py-2 pr-3 sm:table-cell">
        <span
          className={cn(
            'block truncate text-base-sm',
            profile.title !== null ? 'text-text-secondary' : 'text-text-placeholder',
          )}
          title={profile.title ?? undefined}
        >
          {profile.title ?? '—'}
        </span>
      </td>
      <td className="hidden py-2 pr-3 sm:table-cell">
        <span className={cn('font-mono text-sm', engine !== null ? 'text-text-secondary' : 'text-text-placeholder')}>
          {engine ?? '—'}
        </span>
      </td>
      <td className="hidden whitespace-nowrap py-2 pr-3 text-sm text-text-tertiary sm:table-cell">
        {format.ago(profile.reportedAt)}
      </td>
      <td className="py-2 pr-4 text-right" onClick={(event) => event.stopPropagation()}>
        <LaneActions
          profile={profile}
          canCommand={canCommand}
          pending={pending}
          onVerb={(verb) => actions.onLaneVerb(machine.id, profile.profile, verb)}
          onEngine={() => actions.onLaneEngine(machine.id, profile.profile, profile.engine)}
          onLogs={open}
        />
      </td>
    </TableRow>
  );
}

/**
 * A lane's own verbs (fleet-agent ADR 0013) as icons: Stop **or** Start on the lane's reported state — Start only for
 * a lane its host reports stopped by the operator — then Restart, Pin engine and Logs. Every verb needs `canCommand`
 * and is absent, never disabled, without it (ADR 0015); Logs stays for a read-only operator. A verb in flight replaces
 * the verbs with its step: a second press before the first settles is noise the host would answer `already-…`.
 */
export function LaneActions({
  profile,
  canCommand,
  pending,
  onVerb,
  onEngine,
  onLogs,
}: {
  readonly profile: FleetProfileStatus;
  readonly canCommand: boolean;
  readonly pending: FleetCommandView | null;
  readonly onVerb: (verb: FleetLaneCommandVerb) => void;
  readonly onEngine: () => void;
  readonly onLogs: () => void;
}) {
  const tTable = useTranslations('pages.admin.fleet.table');
  const stop = useCommandVerbCopy('stop');
  const start = useCommandVerbCopy('start');
  const restart = useCommandVerbCopy('restart');
  const pin = useCommandVerbCopy('engine');

  const logs = <IconAction icon={TerminalIcon} label={tTable('logs')} onClick={onLogs} />;

  if (!canCommand) return <span className="inline-flex justify-end">{logs}</span>;

  if (pending !== null) return <PendingStep command={pending} />;

  const running = profile.state !== 'stopped';

  return (
    <span className="inline-flex items-center justify-end gap-0.5">
      {running && <IconAction icon={stop.icon} label={stop.label} tone="danger" onClick={() => onVerb('stop')} />}
      {isStoppedByOperator(profile) && (
        <IconAction icon={start.icon} label={start.label} onClick={() => onVerb('start')} />
      )}
      <IconAction icon={restart.icon} label={restart.label} onClick={() => onVerb('restart')} />
      <span className="hidden sm:inline-flex">
        <IconAction icon={pin.icon} label={pin.label} onClick={onEngine} />
      </span>
      {logs}
    </span>
  );
}

/**
 * The host's actions: Stop all **or** Start all (on any lane running; neither with no lanes), Restart all, Choose
 * engine, then the More menu. Enrolment keeps labelled Reject / Approve — rare and consequential, never an icon.
 */
function HostActions({
  machine,
  pending,
  actions,
}: {
  readonly machine: FleetMachineOverview;
  readonly pending: FleetCommandView | null;
  readonly actions: Actions;
}) {
  const t = useTranslations('pages.admin.fleet');
  const tCommand = useTranslations('pages.admin.fleet.command');
  const stopAll = useCommandVerbCopy('stop-all');
  const startAll = useCommandVerbCopy('start-all');
  const restartAll = useCommandVerbCopy('restart-all');

  if (machine.approval === 'pending') {
    return (
      <span className="flex gap-2">
        <Button size="sm" variant="secondary" onClick={() => actions.onApproval(machine.id, 'reject')}>
          {t('reject-cta')}
        </Button>
        <Button size="sm" onClick={() => actions.onApproval(machine.id, 'approve')}>
          {t('approve-cta')}
        </Button>
      </span>
    );
  }

  if (machine.approval !== 'approved') {
    return (
      <Button size="sm" onClick={() => actions.onApproval(machine.id, 'approve')}>
        {t('approve-cta')}
      </Button>
    );
  }

  if (pending !== null) return <PendingStep command={pending} />;

  const hasLanes = machine.profiles.length > 0;
  const anyRunning = machine.profiles.some((profile) => profile.state !== 'stopped');

  return (
    <span className="inline-flex items-center gap-0.5">
      {hasLanes &&
        (anyRunning ? (
          <IconAction
            icon={stopAll.icon}
            label={stopAll.label}
            tone="danger"
            onClick={() => actions.onHostVerb(machine.id, 'stop-all')}
          />
        ) : (
          <IconAction
            icon={startAll.icon}
            label={startAll.label}
            onClick={() => actions.onHostVerb(machine.id, 'start-all')}
          />
        ))}
      {hasLanes && (
        <span className="hidden sm:inline-flex">
          <IconAction
            icon={restartAll.icon}
            label={restartAll.label}
            onClick={() => actions.onHostVerb(machine.id, 'restart-all')}
          />
        </span>
      )}
      <span className="hidden sm:inline-flex">
        <IconAction icon={FlowIcon} label={tCommand('engine-label')} onClick={() => actions.onHostEngine(machine.id)} />
      </span>
      <MoreMenu machine={machine} actions={actions} />
    </span>
  );
}

type MenuItem = { readonly key: string; readonly label: string; readonly danger?: boolean; readonly run: () => void };

/**
 * The host verbs an operator reaches for less often, in three groups: the forms and reports, the agent's own
 * lifecycle with access, and the verbs that delete (last, in danger). On a phone the restart-all and engine icons fold
 * in here too. `force-update-agent` rides `update-agent`'s dialog, as before.
 */
function MoreMenu({ machine, actions }: { readonly machine: FleetMachineOverview; readonly actions: Actions }) {
  const t = useTranslations('pages.admin.fleet');
  const tTable = useTranslations('pages.admin.fleet.table');
  const tCommand = useTranslations('pages.admin.fleet.command');
  const labels = {
    restartAll: useVerbLabel('restart-all'),
    runCommand: useVerbLabel('run-command'),
    preview: useVerbLabel('system-preview'),
    unlock: useVerbLabel('e2e-unlock'),
    restartAgent: useVerbLabel('restart-agent'),
    updateAgent: useVerbLabel('update-agent'),
    prune: useVerbLabel('system-prune'),
  };
  const host = (verb: MachineCommandVerb) => () => actions.onHostVerb(machine.id, verb);
  const form = (verb: FormCommandVerb) => () => actions.onFormVerb(machine.id, verb);

  const phoneOnly: ReadonlyArray<MenuItem> = [
    ...(machine.profiles.length > 0
      ? [{ key: 'restart-all', label: labels.restartAll, run: host('restart-all') }]
      : []),
    { key: 'engine', label: tCommand('engine-label'), run: () => actions.onHostEngine(machine.id) },
  ];
  const groups: ReadonlyArray<ReadonlyArray<MenuItem>> = [
    [
      { key: 'run-command', label: labels.runCommand, run: form('run-command') },
      { key: 'system-preview', label: labels.preview, run: form('system-preview') },
      { key: 'e2e-unlock', label: labels.unlock, run: form('e2e-unlock') },
    ],
    [
      { key: 'restart-agent', label: labels.restartAgent, run: host('restart-agent') },
      { key: 'update-agent', label: labels.updateAgent, run: host('update-agent') },
      { key: 'revoke', label: t('revoke-cta'), run: () => actions.onApproval(machine.id, 'revoke') },
    ],
    [{ key: 'system-prune', label: labels.prune, danger: true, run: host('system-prune') }],
  ];

  return <Popover label={tTable('more-actions', { machine: machine.name })} phoneItems={phoneOnly} groups={groups} />;
}

function Popover({
  label,
  phoneItems,
  groups,
}: {
  readonly label: string;
  readonly phoneItems: ReadonlyArray<MenuItem>;
  readonly groups: ReadonlyArray<ReadonlyArray<MenuItem>>;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; right: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || triggerRef.current === null) return;

    const rect = triggerRef.current.getBoundingClientRect();
    setPosition({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;

      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;

      setOpen(false);
    };

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // The page closes its panel on Escape too; the open menu is the one the operator means.
        event.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', onPointer);
    window.addEventListener('keydown', onKey, true);

    return () => {
      document.removeEventListener('mousedown', onPointer);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  const item = (entry: MenuItem, className?: string) => (
    <Button
      key={entry.key}
      type="button"
      role="menuitem"
      variant="ghost"
      size="sm"
      className={cn('w-full justify-start', entry.danger === true && 'text-danger-700', className)}
      onClick={() => {
        setOpen(false);
        entry.run();
      }}
    >
      {entry.label}
    </Button>
  );

  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-text-secondary"
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <MoreIcon className="h-4 w-4" aria-hidden />
      </Button>
      {open &&
        position !== null &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label={label}
            style={{ top: position.top, right: position.right }}
            className="fixed z-40 flex w-56 flex-col rounded-lg border border-border bg-surface p-1 shadow-lg"
          >
            <div className="flex flex-col border-b border-neutral-100 pb-1 sm:hidden">
              {phoneItems.map((entry) => item(entry))}
            </div>
            {groups.map((entries, index) => (
              <div key={index} className={cn('flex flex-col', index > 0 && 'mt-1 border-t border-neutral-100 pt-1')}>
                {entries.map((entry) => item(entry))}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}

function IconAction({
  icon: Icon,
  label,
  tone,
  onClick,
}: {
  readonly icon: ComponentType<SVGProps<SVGSVGElement>>;
  readonly label: string;
  readonly tone?: 'danger';
  readonly onClick: () => void;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn('h-7 w-7', tone === 'danger' ? 'text-danger-600 hover:bg-danger-50' : 'text-text-secondary')}
      aria-label={label}
      title={label}
      onClick={onClick}
    >
      <Icon className="h-4 w-4" aria-hidden />
    </Button>
  );
}

function PendingStep({ command }: { readonly command: FleetCommandView }) {
  const label = useVerbLabel(command.verb);
  const stepLabel = useCommandStepLabel(commandStep(command));

  return (
    <span
      className="inline-flex items-center justify-end gap-2 whitespace-nowrap text-sm text-text-tertiary"
      role="status"
    >
      <Spinner size="sm" aria-hidden />
      {label} — {stepLabel}
    </span>
  );
}

function MoreIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <circle cx="5" cy="12" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="19" cy="12" r="1.6" />
    </svg>
  );
}

/** The toolbar over the table: the state filter with each state's count, and the lane/ticket search. */
export function MachineTableToolbar({
  machines,
  state,
  query,
  onStateChange,
  onQueryChange,
  trailing,
}: {
  readonly machines: ReadonlyArray<FleetMachineOverview>;
  readonly state: LaneStateFilter;
  readonly query: string;
  readonly onStateChange: (state: LaneStateFilter) => void;
  readonly onQueryChange: (query: string) => void;
  readonly trailing?: ReactNode;
}) {
  const tTable = useTranslations('pages.admin.fleet.table');
  const tState = useTranslations('pages.admin.fleet.state');
  const lanes = machines.flatMap((machine) => machine.profiles);
  const count = (filter: LaneStateFilter) =>
    filter === 'all' ? lanes.length : lanes.filter((lane) => lane.state === filter).length;

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
      <div className="min-w-0 overflow-x-auto">
        <Segments
          ariaLabel={tTable('filter-label')}
          value={state}
          onChange={onStateChange}
          options={LANE_STATE_FILTERS.map((filter) => ({
            value: filter,
            label: `${filter === 'all' ? tTable('all') : tState(filter)} ${count(filter)}`,
          }))}
        />
      </div>
      <span className="hidden flex-1 sm:block" />
      <div className="relative sm:w-64">
        <SearchIcon
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary"
          aria-hidden
        />
        <Input
          value={query}
          aria-label={tTable('search-label')}
          placeholder={tTable('search-placeholder')}
          className="pl-9"
          onChange={(event) => onQueryChange(event.target.value)}
        />
      </div>
      {trailing}
    </div>
  );
}
